import {
  Container,
  Graphics,
  Sprite,
  Text,
  TextStyle,
  Texture,
} from "../../vendor/pixi.min.mjs";
import { BaseScene } from "./BaseScene.js";
import { t, otherLang, setLang, toggleLang } from "../config/i18n.js";

// `globalThis.__PRODUCTION_BUILD__` is replaced with the literal
// `true` by esbuild's `define` (set in scripts/build.js and
// scripts/build-offline.js) at bundle time. In dev (no esbuild)
// the access yields `undefined`, so every `if (!globalThis.__PRODUCTION_BUILD__)`
// block runs normally; in a production bundle it collapses to
// `if (!true)` → `if (false)` and esbuild's dead-code elimination
// drops the block plus everything only referenced inside it
// (Settings password modal, gear button, gear icon helper, …),
// shrinking the offline bundle for end-users who shouldn't see the
// editor at all.
//
// We deliberately reference `globalThis.__PRODUCTION_BUILD__` at
// each call site rather than caching it in a `const` — esbuild's
// minifier doesn't inline single-use module-scope const bindings,
// and a kept `const x = true` defeats the dead-code elimination.

const MENU_ASSETS = {
  background: "assets/menu/background.jpg",
  title: "assets/menu/game-title.png",
  lufi: "assets/menu/menu-lufi.png",
  villamos: "assets/menu/menu-villamos.png",
  // The Train Simulator card replaces the legacy "Menetrend" (pipeline)
  // card. Until a dedicated `menu-trainsim.png` is dropped into
  // assets/menu/, we reuse the existing menetrend art as a placeholder —
  // the card label (i18n key `menu.card_trainsim`) sits on top.
  trainsim: "assets/menu/menu-menetrend.png",
  // Same placeholder reuse pattern as trainsim — until a dedicated
  // `menu-platformer.png` lands in assets/menu/, we render the
  // generic menetrend artwork with the card's label on top.
  platformer: "assets/menu/menu-menetrend.png",
};

const CARDS = [
  {
    key: "lufi",
    scene: "frogger",
    modeGroup: "frogger.settings",
    modeKey: "gameMode",
    defaultMode: "fixed",
    labelKey: "menu.card_lufi",
  },
  {
    key: "villamos",
    scene: "snake",
    modeGroup: "snake.settings",
    modeKey: "gameMode",
    // Stations is the real "tram" experience and matches the schema
    // default in `data/snake.config.js`. The two layers must agree so
    // first-time users boot straight into the stations mode whether
    // they go through the menu card OR a stored theme setting.
    defaultMode: "stations",
    labelKey: "menu.card_villamos",
  },
  { key: "trainsim",   scene: "trainsim",   labelKey: "menu.card_trainsim" },
  { key: "platformer", scene: "platformer", labelKey: "menu.card_platformer" },
];

/**
 * Password gate for the Settings (gear) button on the menu top-bar.
 * Settings + Stage Editor are both reached through here, so we ask
 * for the password once per browser-session and latch the unlock in
 * sessionStorage — that way navigating settings ↔ stage-editor ↔
 * back-to-menu ↔ settings again doesn't keep re-prompting.
 *
 * Session-scoped (not localStorage) on purpose: closing the tab /
 * browser locks settings again, so a kid picking up the device
 * tomorrow can't just walk in.
 */
const SETTINGS_PASSWORD = "7eizoB13";
const SETTINGS_UNLOCK_KEY = "mini-games-settings-unlocked";

function isSettingsUnlocked() {
  try {
    return sessionStorage.getItem(SETTINGS_UNLOCK_KEY) === "1";
  } catch (err) {
    return false;
  }
}

/**
 * Build + show a centred password modal styled to match the menu's
 * purple/gold palette. Returns a Promise that resolves `true` when
 * the user types the correct password (and clicks OK / hits Enter),
 * or `false` when they cancel (Cancel button, Esc, or click outside
 * the card). Wrong-password attempts stay on the modal: the input
 * clears, an inline error message slides in, and the card shakes —
 * no native browser dialogs anywhere.
 */
function showPasswordModal({ expectedPassword }) {
  return new Promise((resolve) => {
    const backdrop = document.createElement("div");
    Object.assign(backdrop.style, {
      position: "fixed",
      inset: "0",
      background: "rgba(15,3,40,0.78)",
      backdropFilter: "blur(4px)",
      webkitBackdropFilter: "blur(4px)",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      zIndex: "10000",
      fontFamily: "Poppins, Arial, sans-serif",
      animation: "ts-pw-fade 0.18s ease-out",
    });

    const card = document.createElement("div");
    Object.assign(card.style, {
      background: "linear-gradient(180deg, #4a1d8a 0%, #3b1670 100%)",
      border: "4px solid #fbbf24",
      // Sharp corners — every popup modal in the app is square
      // per UX direction.
      borderRadius: "0",
      padding: "28px 32px 24px",
      width: "min(420px, 92vw)",
      boxShadow:
        "0 18px 48px rgba(0,0,0,0.55), inset 0 -6px 0 rgba(0,0,0,0.25)",
      color: "#fff",
      boxSizing: "border-box",
      animation: "ts-pw-pop 0.22s cubic-bezier(0.34,1.56,0.64,1)",
    });

    const title = document.createElement("div");
    title.textContent = (t("menu.settings_title") || "SETTINGS").toUpperCase();
    Object.assign(title.style, {
      fontSize: "26px",
      fontWeight: "900",
      color: "#fbbf24",
      textAlign: "center",
      letterSpacing: "3px",
      marginBottom: "6px",
      textShadow: "0 2px 0 rgba(0,0,0,0.45)",
    });

    const subtitle = document.createElement("div");
    subtitle.textContent =
      t("menu.password_prompt") || "Enter password to access Settings:";
    Object.assign(subtitle.style, {
      fontSize: "14px",
      fontWeight: "600",
      color: "rgba(255,255,255,0.82)",
      textAlign: "center",
      marginBottom: "18px",
    });

    const input = document.createElement("input");
    input.type = "password";
    input.autocomplete = "off";
    input.spellcheck = false;
    Object.assign(input.style, {
      width: "100%",
      padding: "12px 16px",
      fontSize: "18px",
      fontWeight: "700",
      background: "#ffffff",
      color: "#3b1670",
      border: "3px solid #fde047",
      borderRadius: "0",
      outline: "none",
      letterSpacing: "3px",
      boxSizing: "border-box",
      fontFamily: "inherit",
      boxShadow: "inset 0 2px 4px rgba(0,0,0,0.12)",
    });
    input.addEventListener("focus", () => {
      input.style.borderColor = "#fbbf24";
      input.style.boxShadow = "0 0 0 3px rgba(251,191,36,0.35)";
    });
    input.addEventListener("blur", () => {
      input.style.borderColor = "#fde047";
      input.style.boxShadow = "inset 0 2px 4px rgba(0,0,0,0.12)";
    });

    const error = document.createElement("div");
    error.textContent = t("menu.password_wrong") || "Wrong password.";
    Object.assign(error.style, {
      fontSize: "13px",
      fontWeight: "700",
      color: "#fca5a5",
      textAlign: "center",
      height: "18px",
      marginTop: "10px",
      opacity: "0",
      transition: "opacity 0.16s ease-out",
    });

    const actions = document.createElement("div");
    Object.assign(actions.style, {
      display: "flex",
      gap: "10px",
      marginTop: "14px",
    });

    const makeBtn = (label, variant) => {
      const b = document.createElement("button");
      b.type = "button";
      b.textContent = label;
      const isPrimary = variant === "primary";
      Object.assign(b.style, {
        flex: "1",
        padding: "12px 16px",
        fontSize: "16px",
        fontWeight: "900",
        letterSpacing: "1.5px",
        borderRadius: "0",
        cursor: "pointer",
        fontFamily: "inherit",
        textTransform: "uppercase",
        transition: "transform 0.08s ease-out, filter 0.12s",
        background: isPrimary ? "#fbbf24" : "rgba(255,255,255,0.10)",
        color: isPrimary ? "#3b1670" : "#ffffff",
        border: isPrimary
          ? "3px solid #3b1670"
          : "3px solid rgba(255,255,255,0.25)",
        boxShadow: isPrimary
          ? "0 4px 0 rgba(0,0,0,0.25)"
          : "0 4px 0 rgba(0,0,0,0.20)",
      });
      b.addEventListener("mouseenter", () => {
        b.style.filter = "brightness(1.08)";
      });
      b.addEventListener("mouseleave", () => {
        b.style.filter = "";
        b.style.transform = "";
      });
      b.addEventListener("mousedown", () => {
        b.style.transform = "translateY(2px)";
      });
      b.addEventListener("mouseup", () => {
        b.style.transform = "";
      });
      return b;
    };

    const cancelBtn = makeBtn(t("common.cancel") || "Cancel", "ghost");
    const okBtn = makeBtn(t("common.ok") || "OK", "primary");

    actions.appendChild(cancelBtn);
    actions.appendChild(okBtn);

    card.appendChild(title);
    card.appendChild(subtitle);
    card.appendChild(input);
    card.appendChild(error);
    card.appendChild(actions);
    backdrop.appendChild(card);

    // Inject keyframes for entry animations once per document. We
    // can't use Element.animate() here because the entry animation
    // needs to run as soon as the elements are inserted; setting it
    // via cssText/style is the cleanest path.
    if (!document.getElementById("ts-pw-keyframes")) {
      const style = document.createElement("style");
      style.id = "ts-pw-keyframes";
      style.textContent = `
        @keyframes ts-pw-fade { from { opacity: 0; } to { opacity: 1; } }
        @keyframes ts-pw-pop {
          0%   { transform: scale(0.85); opacity: 0; }
          100% { transform: scale(1);    opacity: 1; }
        }
      `;
      document.head.appendChild(style);
    }

    document.body.appendChild(backdrop);
    setTimeout(() => input.focus(), 30);

    const close = (result) => {
      document.removeEventListener("keydown", onKey);
      if (backdrop.parentNode) backdrop.parentNode.removeChild(backdrop);
      resolve(result);
    };

    const shake = () => {
      card.animate(
        [
          { transform: "translateX(0)" },
          { transform: "translateX(-9px)" },
          { transform: "translateX(9px)" },
          { transform: "translateX(-7px)" },
          { transform: "translateX(7px)" },
          { transform: "translateX(0)" },
        ],
        { duration: 340, easing: "ease-out" }
      );
    };

    const submit = () => {
      if (input.value === expectedPassword) {
        close(true);
        return;
      }
      error.style.opacity = "1";
      shake();
      input.value = "";
      input.focus();
    };

    const onKey = (e) => {
      if (e.key === "Enter") { e.preventDefault(); submit(); }
      if (e.key === "Escape") { e.preventDefault(); close(false); }
    };
    document.addEventListener("keydown", onKey);

    okBtn.addEventListener("click", submit);
    cancelBtn.addEventListener("click", () => close(false));
    backdrop.addEventListener("click", (e) => {
      if (e.target === backdrop) close(false);
    });
  });
}

/**
 * Prompt the user for the settings password. Returns a Promise that
 * resolves `true` when the gate is now open (either already unlocked
 * this session, or the user just typed the correct password), and
 * `false` when the user cancels. Wrong-password attempts stay on the
 * modal; only Cancel / Esc / outside-click resolve to `false`.
 */
async function tryUnlockSettings() {
  if (isSettingsUnlocked()) return true;
  const ok = await showPasswordModal({ expectedPassword: SETTINGS_PASSWORD });
  if (ok) {
    try { sessionStorage.setItem(SETTINGS_UNLOCK_KEY, "1"); } catch {}
    return true;
  }
  return false;
}

const TITLE_TARGET_W = 780;
const TITLE_TOP_PAD = 40;
const TITLE_CARDS_GAP = 30;
const CARDS_TARGET_H = 620;
const CARDS_BOTTOM_PAD = 60;
const CARD_GAP = 56;

const HOVER_SCALE = 1.04;
const PRESS_SCALE = 0.96;
const SCALE_LERP = 0.22;
const BOB_AMPLITUDE = 6;
const BOB_PERIOD_MS = 2400;

const LANG_STYLE = new TextStyle({
  fontFamily: "Poppins, Arial",
  fontSize: 22,
  fontWeight: "800",
  fill: "#3b1670",
  letterSpacing: 1,
});

const TITLE_LINE1_STYLE = new TextStyle({
  fontFamily: "Poppins, Arial",
  fontSize: 78,
  fontWeight: "900",
  fill: "#ffffff",
  align: "center",
  letterSpacing: 2,
  dropShadow: {
    color: "#1a0838",
    alpha: 0.65,
    blur: 6,
    distance: 4,
    angle: Math.PI / 4,
  },
});

const TITLE_LINE2_STYLE = new TextStyle({
  fontFamily: "Poppins, Arial",
  fontSize: 88,
  fontWeight: "900",
  fill: "#fbbf24",
  align: "center",
  letterSpacing: 2,
  dropShadow: {
    color: "#1a0838",
    alpha: 0.65,
    blur: 6,
    distance: 4,
    angle: Math.PI / 4,
  },
});

const CARD_LABEL_STYLE = new TextStyle({
  fontFamily: "Poppins, Arial",
  fontSize: 46,
  fontWeight: "900",
  fill: "#3b1670",
  align: "center",
  letterSpacing: 3,
  dropShadow: {
    color: "#ffffff",
    alpha: 0.35,
    blur: 0,
    distance: 2,
    angle: Math.PI / 2,
  },
});

const _textureCache = new Map();

function loadTexture(src) {
  if (_textureCache.has(src)) return Promise.resolve(_textureCache.get(src));
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      const tex = Texture.from(img);
      if (tex?.source) {
        tex.source.scaleMode = "linear";
        tex.source.autoGenerateMipmaps = true;
      }
      _textureCache.set(src, tex);
      resolve(tex);
    };
    img.onerror = () => reject(new Error(`Failed to load ${src}`));
    img.src = src;
  });
}

export class MenuScene extends BaseScene {
  _bgSprite = null;
  _title = null;
  _titleLine1 = null;
  _titleLine2 = null;
  _cards = [];
  _topBar = null;
  _langBtn = null;
  _langLabel = null;
  _gearBtn = null;
  _updateCallback = null;
  _startTime = 0;
  // Bumped on every init()/destroy() so a stale async _loadAndBuild from a
  // prior activation resolves into a no-op instead of mutating the live scene
  // (the MenuScene is a singleton reused on every back-to-menu transition).
  _loadToken = 0;
  // Synchronous placeholder painted in init() so the canvas's dark navy
  // background never flashes through while textures are still resolving.
  _placeholder = null;

  init(game) {
    super.init(game);
    this._startTime = performance.now();
    this._loadToken++;
    const token = this._loadToken;

    // Honour the `shared.settings.availableLanguages` setting before
    // any label is constructed, so the title + card text render in
    // the forced locale on the very first paint. When the setting is
    // 'both' we leave whatever lang Game.js seeded from localStorage.
    this._applyLanguageRestriction();

    this._paintPlaceholder();

    this._loadAndBuild(token).catch((err) => {
      console.error("[Menu] Asset load failed:", err);
    });

    this._updateCallback = (delta) => this._tick(delta);
    this.game.onUpdate(this._updateCallback);
  }

  /**
   * Force-pin the i18n language when the operator has restricted the
   * deployment to a single locale. Two layers checked in order:
   *
   *   1. Build-time `__BUILD_LANG_MODE__` — set by esbuild from
   *      `data/shared.config.js`. 'hu' / 'en' means the bundle is
   *      hard-locked at compile time; the pill code was DCE'd out.
   *   2. Runtime setting `availableLanguages` — the in-app picker
   *      under Settings → General. Same semantics.
   *
   * Build-time wins, so a single-locale build stays single-locale
   * even if localStorage says otherwise. When both layers say 'both'
   * this is a no-op and the menu's language pill stays interactive.
   *
   * Returns the resolved restriction so callers (e.g. `_buildTopBar`)
   * can decide whether to skip constructing the language pill.
   */
  _applyLanguageRestriction() {
    const buildLangMode = typeof globalThis.__BUILD_LANG_MODE__ !== 'undefined'
      ? globalThis.__BUILD_LANG_MODE__
      : 'both';
    if (buildLangMode === 'hu' || buildLangMode === 'en') {
      setLang(buildLangMode);
      this.game?.theme?.setLang?.(buildLangMode);
      return buildLangMode;
    }
    const restriction = this.game?.theme?.getSetting?.(
      "shared.settings",
      "availableLanguages",
      "both",
    );
    if (restriction === "hu" || restriction === "en") {
      setLang(restriction);
      this.game?.theme?.setLang?.(restriction);
    }
    return restriction;
  }

  destroy() {
    this._loadToken++;
    if (this._updateCallback) {
      this.game.offUpdate(this._updateCallback);
      this._updateCallback = null;
    }
    for (const c of this._cards) {
      c.sprite.off?.("pointerover");
      c.sprite.off?.("pointerout");
      c.sprite.off?.("pointerdown");
      c.sprite.off?.("pointerup");
      c.sprite.off?.("pointerupoutside");
      c.sprite.off?.("pointertap");
    }
    this._cards = [];
    this._gearBtn?.off?.("pointertap");
    this._langBtn?.off?.("pointertap");
    this._gearBtn = null;
    this._langBtn = null;
    this._langLabel = null;
    this._title = null;
    this._titleLine1 = null;
    this._titleLine2 = null;
    this._bgSprite = null;
    this._topBar = null;
    this._clearPlaceholder();
    super.destroy();
  }

  _paintPlaceholder() {
    this._clearPlaceholder();
    const g = new Graphics();
    g.rect(0, 0, this.game.width, this.game.height);
    g.fill(0x3b1670);
    this._placeholder = g;
    this.container.addChildAt(g, 0);
  }

  _clearPlaceholder() {
    if (!this._placeholder) return;
    if (this._placeholder.parent) {
      this._placeholder.parent.removeChild(this._placeholder);
    }
    this._placeholder.destroy?.();
    this._placeholder = null;
  }

  async _loadAndBuild(token) {
    const [bgTex, titleTex, lufiTex, villamosTex, trainsimTex, platformerTex] =
      await Promise.all([
        loadTexture(MENU_ASSETS.background),
        loadTexture(MENU_ASSETS.title),
        loadTexture(MENU_ASSETS.lufi),
        loadTexture(MENU_ASSETS.villamos),
        loadTexture(MENU_ASSETS.trainsim),
        loadTexture(MENU_ASSETS.platformer),
      ]);

    if (token !== this._loadToken) return;

    this._clearPlaceholder();
    this._buildBackground(bgTex);
    this._buildCards({
      lufi: lufiTex,
      villamos: villamosTex,
      trainsim: trainsimTex,
      platformer: platformerTex,
    });
    this._buildTitle(titleTex);
    this._buildTopBar();
    this._layoutAll();
  }

  _buildBackground(tex) {
    const sprite = new Sprite(tex);
    sprite.anchor.set(0.5);
    const w = this.game.width;
    const h = this.game.height;
    const scale = Math.max(w / tex.width, h / tex.height);
    sprite.scale.set(scale);
    sprite.x = w / 2;
    sprite.y = h / 2;
    this._bgSprite = sprite;
    this.container.addChildAt(sprite, 0);
  }

  _buildTitle(tex) {
    const sprite = new Sprite(tex);
    sprite.anchor.set(0.5);
    const scale = TITLE_TARGET_W / tex.width;
    sprite.scale.set(scale);
    this._title = sprite;
    this.container.addChild(sprite);

    this._titleLine1 = new Text({
      text: t("menu.title_line1"),
      style: TITLE_LINE1_STYLE,
    });
    this._titleLine1.anchor.set(0.5);
    this.container.addChild(this._titleLine1);

    this._titleLine2 = new Text({
      text: t("menu.title_line2"),
      style: TITLE_LINE2_STYLE,
    });
    this._titleLine2.anchor.set(0.5);
    this.container.addChild(this._titleLine2);
  }

  _buildCards(textures) {
    for (const def of CARDS) {
      // Platformer is still in development — it's only shown in dev
      // builds. esbuild rewrites `globalThis.__PRODUCTION_BUILD__` to
      // `true` in production / offline bundles, so this collapses to a
      // hard skip there and the menu ships with just the three finished
      // games (no 4th card).
      if (def.scene === "platformer" && globalThis.__PRODUCTION_BUILD__) continue;

      const tex = textures[def.key];
      // Guard against a missing/failed texture: skipping the card keeps
      // the rest of the menu (title + other cards) rendering instead of
      // throwing out of the whole build and leaving a blank screen.
      if (!tex) {
        console.warn(`[Menu] Missing texture for card "${def.key}" — skipping.`);
        continue;
      }
      const wrap = new Container();
      wrap.eventMode = "static";
      wrap.cursor = "pointer";

      const sprite = new Sprite(tex);
      sprite.anchor.set(0.5);
      const scale = CARDS_TARGET_H / tex.height;
      sprite.scale.set(scale);
      wrap.addChild(sprite);

      const label = new Text({
        text: t(def.labelKey),
        style: CARD_LABEL_STYLE,
      });
      label.anchor.set(0.5);
      label.x = 0;
      label.y = -CARDS_TARGET_H * 0.39;
      wrap.addChild(label);

      const card = {
        def,
        sprite: wrap,
        inner: sprite,
        label,
        baseScale: scale,
        targetScale: 1,
        currentScale: 1,
        hovered: false,
        pressed: false,
        bobPhase: Math.random() * Math.PI * 2,
        baseY: 0,
      };

      wrap.on("pointerover", () => {
        card.hovered = true;
      });
      wrap.on("pointerout", () => {
        card.hovered = false;
        card.pressed = false;
      });
      wrap.on("pointerdown", () => {
        card.pressed = true;
      });
      wrap.on("pointerup", () => {
        card.pressed = false;
      });
      wrap.on("pointerupoutside", () => {
        card.pressed = false;
      });
      wrap.on("pointertap", () => this._activateCard(card));

      this._cards.push(card);
      this.container.addChild(wrap);
    }
  }

  _activateCard(card) {
    const def = card.def;
    if (def.modeGroup && def.modeKey) {
      const mode =
        this.game.theme?.getSetting?.(
          def.modeGroup,
          def.modeKey,
          def.defaultMode,
        ) || def.defaultMode;
      if (def.scene === "frogger") this.game.froggerMode = mode;
      if (def.scene === "snake") this.game.snakeMode = mode;
    }
    if (!this.game.sceneManager._scenes.has(def.scene)) {
      console.warn(`[Menu] Scene "${def.scene}" not registered`);
      return;
    }
    this.game.sceneManager.switchScene(def.scene);
  }

  _buildTopBar() {
    const bar = new Container();

    // ── Settings (gear) button ──────────────────────────────────
    // Production / offline builds strip this entirely (along with
    // the password modal, OptionsScene and StageEditorScene) so the
    // bundle ships without any editor surface. esbuild's `define`
    // rewrites `globalThis.__PRODUCTION_BUILD__` to `true` at bundle
    // time, letting dead-code elimination drop the whole branch +
    // every helper it references (showPasswordModal,
    // tryUnlockSettings, isSettingsUnlocked, password constants).
    if (!globalThis.__PRODUCTION_BUILD__) {
      const gear = new Graphics();
      this._drawPillBg(gear, 56, 56, 28, 0xfde047, 0x3b1670);
      gear.eventMode = "static";
      gear.cursor = "pointer";
      const gearIcon = new Graphics();
      this._drawGearIcon(gearIcon, 28, 28, 13, 0x3b1670);
      gear.addChild(gearIcon);
      gear.on("pointertap", async () => {
        // Settings is password-protected. The Stage Editor used to live
        // here as its own pill (dev-only `?editor=1`) — it now lives
        // INSIDE Settings, so unlocking the gate is the single
        // entry point to both surfaces. `tryUnlockSettings` shows the
        // styled DOM password modal and resolves with the unlock
        // result; we await it so we don't switch scenes on cancel.
        const unlocked = await tryUnlockSettings();
        if (!unlocked) return;
        this.game.sceneManager.switchScene("options");
      });
      this._gearBtn = gear;
      bar.addChild(gear);
    }

    // ── Language pill (HU ⇄ EN) ─────────────────────────────────
    // Built only when the bundle isn't pinned to a single locale.
    // We check both the build-time __BUILD_LANG_MODE__ define AND
    // the runtime `availableLanguages` setting; either one being
    // 'hu' or 'en' suppresses the pill. The build-time check wins
    // first, which means a 'hu' or 'en' build never even includes
    // the pill construction code (esbuild + minify DCE strip the
    // whole `if` body since the literal compares as false).
    // `_applyLanguageRestriction` (called from init()) has already
    // pinned the i18n locale at this point.
    const buildLangMode = typeof globalThis.__BUILD_LANG_MODE__ !== 'undefined'
      ? globalThis.__BUILD_LANG_MODE__
      : 'both';
    const langRestriction = buildLangMode !== 'both'
      ? buildLangMode
      : this.game?.theme?.getSetting?.(
          "shared.settings",
          "availableLanguages",
          "both",
        );
    if (langRestriction !== "hu" && langRestriction !== "en") {
      const langW = 92;
      const lang = new Graphics();
      this._drawPillBg(lang, langW, 56, 28, 0xffffff, 0x3b1670);
      lang.eventMode = "static";
      lang.cursor = "pointer";
      const label = new Text({
        // Show the language you can switch TO (on HU show "EN", vice versa).
        text: otherLang().toUpperCase(),
        style: LANG_STYLE,
      });
      label.anchor.set(0.5);
      label.x = langW / 2;
      label.y = 28;
      lang.addChild(label);
      lang.on("pointertap", () => {
        const newLang = toggleLang();
        if (this.game.theme) this.game.theme.setLang?.(newLang);
        this.destroy();
        this.container.removeChildren();
        this.init(this.game);
      });
      this._langBtn = lang;
      this._langLabel = label;
      bar.addChild(lang);
    }

    // The Stage Editor used to live here as its own pill behind a
    // `?editor=1` dev flag. It's now a button inside the Settings
    // scene, so the gear icon (password-gated) is the single entry
    // point for both Settings and the editor.

    this._topBar = bar;
    this.container.addChild(bar);
  }

  _drawPillBg(g, w, h, r, fill, stroke) {
    g.roundRect(0, 0, w, h, r);
    g.fill(fill);
    if (stroke != null) {
      g.roundRect(0, 0, w, h, r);
      g.stroke({ color: stroke, width: 3, alignment: 1 });
    }
  }

  _drawGearIcon(g, cx, cy, radius, color) {
    const teeth = 8;
    const outerR = radius;
    const innerR = radius * 0.62;
    const toothDepth = radius * 0.32;
    const toothHalfAngle = (Math.PI / teeth) * 0.42;
    const points = [];
    for (let i = 0; i < teeth; i++) {
      const angle = (i / teeth) * Math.PI * 2;
      const r = outerR + toothDepth;
      points.push(
        cx + Math.cos(angle - toothHalfAngle) * r,
        cy + Math.sin(angle - toothHalfAngle) * r,
      );
      points.push(
        cx + Math.cos(angle + toothHalfAngle) * r,
        cy + Math.sin(angle + toothHalfAngle) * r,
      );
      const mid = angle + Math.PI / teeth;
      points.push(
        cx + Math.cos(mid - toothHalfAngle) * outerR,
        cy + Math.sin(mid - toothHalfAngle) * outerR,
      );
      points.push(
        cx + Math.cos(mid + toothHalfAngle) * outerR,
        cy + Math.sin(mid + toothHalfAngle) * outerR,
      );
    }
    g.moveTo(points[0], points[1]);
    for (let i = 2; i < points.length; i += 2)
      g.lineTo(points[i], points[i + 1]);
    g.closePath();
    g.fill(color);
    g.circle(cx, cy, innerR);
    g.cut();
    g.circle(cx, cy, innerR * 0.4);
    g.fill(color);
  }

  _layoutAll() {
    if (!this.game) return;
    const w = this.game.width;
    const h = this.game.height;
    const cx = w / 2;

    let titleBottom = TITLE_TOP_PAD;
    if (this._title) {
      this._title.x = cx;
      this._title.y = TITLE_TOP_PAD + this._title.height / 2;
      titleBottom = this._title.y + this._title.height / 2;

      if (this._titleLine1 && this._titleLine2) {
        const titleH = this._title.height;
        this._titleLine1.x = cx;
        this._titleLine1.y = this._title.y - titleH * 0.18 + 20;
        this._titleLine2.x = cx;
        this._titleLine2.y = this._title.y + titleH * 0.15 + 15;
      }
    }

    if (this._cards.length) {
      const cardH = this._cards[0].sprite.height;
      const availableTop = titleBottom + TITLE_CARDS_GAP;
      const availableBottom = h - CARDS_BOTTOM_PAD;
      const availableMid = (availableTop + availableBottom) / 2;
      const rowCy = Math.max(availableTop + cardH / 2, availableMid);

      const cardWs = this._cards.map((c) => c.sprite.width);
      const totalW =
        cardWs.reduce((a, b) => a + b, 0) + CARD_GAP * (this._cards.length - 1);
      let x = cx - totalW / 2;
      for (let i = 0; i < this._cards.length; i++) {
        const c = this._cards[i];
        const cw = cardWs[i];
        c.sprite.x = x + cw / 2;
        c.sprite.y = rowCy;
        c.baseY = rowCy;
        x += cw + CARD_GAP;
      }
    }

    if (this._topBar) {
      const margin = 32;
      // Both pills are optional (gear stripped in production builds,
      // language pill stripped when the deployment is locked to a
      // single locale). Walk from the right edge inward so whichever
      // combination ends up present still hugs the corner.
      let nextRight = w - margin;
      if (this._gearBtn) {
        this._gearBtn.x = nextRight - 56;
        this._gearBtn.y = margin;
        nextRight -= 56 + 12;
      }
      if (this._langBtn) {
        this._langBtn.x = nextRight - 92;
        this._langBtn.y = margin;
      }
    }
  }

  _tick(delta) {
    if (!this._cards.length) return;

    const tNow = performance.now();
    for (const c of this._cards) {
      const target = c.pressed ? PRESS_SCALE : c.hovered ? HOVER_SCALE : 1;
      c.targetScale += (target - c.targetScale) * SCALE_LERP;
      const s = c.baseScale * c.targetScale;
      c.inner.scale.set(s);
      if (c.label) c.label.scale.set(c.targetScale);

      const phase =
        ((tNow - this._startTime) / BOB_PERIOD_MS) * Math.PI * 2 + c.bobPhase;
      const bob = Math.sin(phase) * BOB_AMPLITUDE;
      c.sprite.y = c.baseY + bob;
    }
  }
}
